import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { cn } from "../lib/cn";

/**
 * Button — single source of truth for every interactive CTA across Ando v2.
 *
 * SOTA design system (May 2026 refresh). Replaces the legacy black-gradient
 * pattern (`bg-gradient-to-b from-ink-2 to-[#3A3835]`) which is now banned —
 * grep for it after every refactor. Brand orange owns the primary CTA, every
 * secondary surface is neutral glass, ghost is reserved for inline/dismiss.
 *
 * Variants — choose by intent, never by location:
 * - `primary`     → the ONE high-value CTA per surface (orange gradient).
 *                   Examples: Componi, Invia, Continua nel browser, Triage,
 *                   Autorizza, Crea task.
 * - `secondary`   → neutral glass action that supports the primary. Examples:
 *                   Annulla, Modifica campi, Riprogramma, Inoltra (when not the
 *                   main CTA), Crea task (when not the main CTA).
 * - `ghost`       → transparent inline action, often icon-only. Examples:
 *                   Chiudi, Vedi tutto, header icon-bar entries.
 * - `destructive` → soft-rose surface for destructive intent (Archivia,
 *                   Elimina). Hover deepens to signal commitment.
 *
 * Sizes follow the Ando 8px chrome ladder.
 *
 * Forward-refs the underlying <button> so Radix / floating-ui `asChild`
 * patterns work transparently.
 */
const buttonVariants = cva(
  // Base — focus ring (a11y P0), motion budget (200ms ease-out), svg sizing,
  // disabled handling. Sticks to brand tokens so dark-mode flips at the
  // CSS-variable level without per-component overrides.
  [
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap",
    "select-none rounded-md font-medium",
    "transition-[transform,background-color,box-shadow,filter] duration-200 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-2 focus-visible:ring-offset-cream",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        // Primary — orange brand gradient with hairline highlight + warm shadow.
        // The inset rgba(255,255,255,0.18) line ties it to the cream/glass
        // surfaces sitting beneath it; the warm shadow keeps it grounded
        // without competing with content shadows.
        primary: [
          "bg-gradient-to-b from-[#FF7A1A] to-[#E5651A] text-white",
          "shadow-[0_1px_2px_rgba(255,122,26,0.18),0_4px_12px_-4px_rgba(255,122,26,0.32),inset_0_1px_0_rgba(255,255,255,0.18)]",
          "hover:-translate-y-px hover:brightness-[1.04]",
          "hover:shadow-[0_1px_2px_rgba(255,122,26,0.2),0_6px_16px_-4px_rgba(255,122,26,0.38),inset_0_1px_0_rgba(255,255,255,0.22)]",
          "active:translate-y-0 active:brightness-100",
          "disabled:hover:translate-y-0 disabled:hover:brightness-100",
        ].join(" "),
        // Secondary — the brand "pencil line": orange outline, empty fill,
        // orange text. Echoes the line logo; the voice for SECONDARY actions
        // (support a primary, filters, chips). Primaries STAY filled
        // (`primary`) — a hollow primary reads as disabled (the "non si vede
        // niente" regression). For neutral / cancel actions prefer `ghost`.
        secondary: [
          "bg-transparent text-ando-orange",
          "border-[1.5px] border-ando-orange/70",
          // Warm halo — same orange glow as the logo, via a soft outer shadow.
          "shadow-[0_2px_12px_-2px_rgba(255,122,26,0.28)]",
          "hover:-translate-y-px hover:border-ando-orange hover:bg-ando-orange/[0.08]",
          "hover:shadow-[0_4px_16px_-2px_rgba(255,122,26,0.4)]",
          "active:translate-y-0 active:bg-ando-orange/[0.12]",
        ].join(" "),
        // Ghost — zero chrome. Used for inline dismiss / utility actions and
        // icon-only buttons in toolbars.
        ghost: ["bg-transparent text-ink-2", "hover:bg-black/[0.04] hover:text-ink-1"].join(" "),
        // Destructive — soft rose surface. Hard rose only on hover to make the
        // commitment visible. Never combine with primary on the same row.
        destructive: [
          "border border-rose-200 bg-rose-50 text-rose-700",
          "shadow-[0_1px_2px_rgba(225,29,72,0.06)]",
          "hover:-translate-y-px hover:border-rose-300 hover:bg-rose-100",
          "active:translate-y-0",
          "focus-visible:ring-rose-400/45",
        ].join(" "),
        // ── Backward-compat aliases (deprecated) ──────────────────────────
        // The May 2026 button refresh consolidated to 4 variants. These
        // aliases keep the wider codebase compiling while consumers migrate.
        // Do NOT add new aliases — pick one of primary/secondary/ghost/destructive.
        /** @deprecated Use `primary`. */
        default: [
          "bg-gradient-to-b from-[#FF7A1A] to-[#E5651A] text-white",
          "shadow-[0_1px_2px_rgba(255,122,26,0.18),0_4px_12px_-4px_rgba(255,122,26,0.32),inset_0_1px_0_rgba(255,255,255,0.18)]",
          "hover:-translate-y-px hover:brightness-[1.04]",
          "active:translate-y-0 active:brightness-100",
        ].join(" "),
        /** @deprecated Use `secondary`. */
        outline: [
          "bg-white/85 text-ink-1 backdrop-blur-md",
          "border border-black/[0.08]",
          "hover:border-black/[0.12] hover:bg-white/95",
        ].join(" "),
        /** @deprecated Use `ghost` for inline link semantics. */
        link: "bg-transparent text-ando-orange underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-7 px-3 text-[11px] [&_svg]:size-3",
        md: "h-8 px-3 text-[12px]",
        lg: "h-10 px-4 text-[13px] [&_svg]:size-4",
        // Square icon-only — used with `aria-label` for a11y.
        icon: "h-8 w-8 [&_svg]:size-3.5",
        /** @deprecated Use `sm`. */
        xs: "h-7 rounded-sm px-2 text-[11px] [&_svg]:size-3",
        /** @deprecated Use `md`. Maps to the v1 default 9×16 chrome. */
        default: "h-9 px-4 text-sm",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as a Radix `Slot` child instead of a native <button>. */
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type ?? "button"}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
